import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { cn } from "utils";
import useApp from "hooks/useApp";
import { CHANGE_STATE } from "reducers/app.reducer";
import axiosInstance from "../axios/axiosInstance";
import routes from "constants/routes";
import { formatRoute } from "utils";
import TosModal, { hasTosAccepted } from "../components/TosModal";
import { getActiveCompanyId, isCompanyOptionalPath, mustSelectCompany } from "utils/companyContext";

const PendingApprovalBanner = () => {
    const [dismissed, setDismissed] = useState(false);
    const navigate = useNavigate();
    const companyId = getActiveCompanyId();
    const userRole = localStorage.getItem('userRole') || '';

    // Location managers should not see company-wide approval noise
    const showBanner = userRole !== 'Location';

    const { data: pendingAccounts = [] } = useQuery({
        queryKey: ['pending-approvals', companyId],
        queryFn: async () => {
            const res = await axiosInstance.get(`/auth/pending-approvals/${companyId}`);
            return res.data ?? [];
        },
        enabled: !!companyId && showBanner,
        refetchInterval: 30000,
    });

    const count = pendingAccounts.length;
    if (!showBanner || dismissed || count === 0) return null;

    const names = pendingAccounts
        .map(p => p.displayName || [p.firstName, p.lastName].filter(Boolean).join(' ') || p.email)
        .filter(Boolean);
    const namePreview = names.slice(0, 3).join(', ');
    const more = names.length > 3 ? ` +${names.length - 3} more` : '';

    return (
        <div style={{
            background: '#fef9c3', border: '1px solid #fbbf24',
            padding: '10px 20px', display: 'flex', alignItems: 'center',
            gap: 12, fontSize: 13, color: '#78350f', flexShrink: 0,
        }}>
            <span style={{ fontSize: 16 }}>⏳</span>
            <span style={{ flex: 1 }}>
                <strong>{count} pending onboarding{count !== 1 ? 's' : ''}</strong> awaiting your approval
                {namePreview ? <>: <strong>{namePreview}{more}</strong></> : '.'}
            </span>
            <button
                onClick={() => navigate(formatRoute(routes.ONBOARDING.HOME))}
                style={{
                    background: '#1B75F0', color: '#fff', border: 'none',
                    borderRadius: 6, padding: '5px 14px', fontWeight: 600,
                    fontSize: 12, cursor: 'pointer',
                }}
            >
                Review →
            </button>
            <button
                onClick={() => setDismissed(true)}
                title="Dismiss"
                style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#92400e', fontSize: 18, lineHeight: 1, padding: '0 4px',
                }}
            >
                ×
            </button>
        </div>
    );
};

const DashboardLayout = () => {
    const { isSidebarOpen, dispatch } = useApp();
    const [tosAccepted, setTosAccepted] = useState(hasTosAccepted);
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const isMobile = () => window.innerWidth <= 768;

    // Auto-close sidebar on mobile at mount
    useEffect(() => {
        if (isMobile()) {
            dispatch({ type: CHANGE_STATE, payload: { isSidebarOpen: false } });
        }
    }, []);

    // Close sidebar on route change (mobile)
    useEffect(() => {
        if (isMobile()) {
            dispatch({ type: CHANGE_STATE, payload: { isSidebarOpen: false } });
        }
    }, [pathname]);

    const needsCompany = mustSelectCompany();
    const onOptionalPath = isCompanyOptionalPath(pathname);
    // Block company-scoped pages before first paint so queries never fire with companyId=null.
    const blockCompanyPages = needsCompany && !onOptionalPath;

    // Accountants / company users with no selected company: stay on My Companies
    useEffect(() => {
        if (!blockCompanyPages) return;
        navigate("/dashboard/my-companies", { replace: true });
    }, [blockCompanyPages, navigate]);

    const closeSidebar = () => dispatch({ type: CHANGE_STATE, payload: { isSidebarOpen: false } });

    return (
        <div className="dashboard container-fluid p-0">
            {!tosAccepted && <TosModal onAccept={() => setTosAccepted(true)} />}
            {/* Mobile backdrop */}
            {isSidebarOpen && (
                <div className="mobile-sidebar-backdrop" onClick={closeSidebar} style={{ display: 'none' }} />
            )}
            <div className="app-container d-flex">
                <Sidebar />
                <div className={cn("main-content header-container", isSidebarOpen ? "open" : "closed")}>
                    <Header />
                    <PendingApprovalBanner />
                    <div className="content">
                        {blockCompanyPages ? null : <Outlet />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardLayout;
